import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  Row,
  Col,
  Table,
  Button,
  Spin,
  Popover,
  Avatar,
  Space,
  Empty,
} from "antd";
import {
  DownloadOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import store from "./store/store";
import {
  registryProjectDetailsApi,
  useGetRegistryProjectDetailsQuery,
} from "./store/registryprojectdetailsapi";
import { registryMethodApi } from "./store/registrymethodapi";
import { RegistryDisplayOrganizationName } from "./RegistryDisplayOrgName";
import { registryDocumentApi } from "./store/registrydocumentapi";
import axios from "axios";
import { registryFinalTokenApi } from "./store/registryfinaltokenapi";
import moment from "moment";
import { ColumnsType } from "antd/es/table";

interface RouteParams {
  id: string;
}

interface Assignment {
  assignee_type: string;
  organization: string;
}

interface ProjectData {
  _id: string;
  name: string;
  ghg_project_summary: string;
  method_id: string | string[];
  status: string;
  organization: string;
  standards_used?: string;
  crediting_period?: string[];
  project_period?: string[];
  assignments?: Assignment[];
}

interface ProjectDetailsResponse {
  data: ProjectData[];
}

interface MethodData {
  name: string;
}

interface YieldIdItem {
  token_id: string;
  co2_realized: number;
}

interface YieldData {
  period: string;
  co2_estimated: number;
  co2_realized: number;
  yield_id?: YieldIdItem[];
  pre_credits: number;
  credits: number;
  storage_duration: number;
}

interface DocumentData {
  ID: string;
  doctype: string;
  filename: string;
  hash: string;
}

interface DateObject {
  $date: string;
}

interface CertificateDetail {
  updated?: DateObject;
  created?: DateObject;
  finaltoken_id: string;
  no_of_token: number;
  beneficiary_name: string;
  certificate_retirement_purpose: string;
  yield_period?: string[];
}

interface CertificateDetailsResponse {
  data: CertificateDetail[];
}

const RegistryProjectDetailsPageView: React.FC = () => {
  const { id } = useParams<RouteParams>();
  console.log(id);
  const history = useHistory();

  const [isSpinning, setIsSpinning] = useState<boolean>(false);

  const [methodData, setMethodData] = useState<MethodData[]>();
  const [documentList, setDocumentList] = useState<DocumentData[]>();
  const [yieldDataList, setYieldDataList] = useState<YieldData[]>();
  const [certificateDetails, setCertificateDetails] =
    useState<CertificateDetailsResponse>();
  const { data, isLoading } = useGetRegistryProjectDetailsQuery(id, {
    skip: !id,
  });

  const handleGoBack = (): void => {
    history.push("/");
  };

  async function fetchData(): Promise<void> {
    try {
      setIsSpinning(true);

      const methodId = data?.data?.[0]?.method_id;
      const methodIdArray = Array.isArray(methodId) ? methodId : (methodId ? [methodId] : []);

      const { data: methodDetails, isLoading: isMethodDetailsLoading } =
        await store.dispatch(
          registryMethodApi.endpoints.getRegistryBulkMethods.initiate(
            methodIdArray
          )
        );

      const { data: yieldData } = await store.dispatch(
        registryProjectDetailsApi.endpoints.getRegistryProjectYieldDetails.initiate(
          data?.data?.[0]?._id
        )
      );

      // Extracting Final Token using project_id
      const { data: finalTokenData } =
        await store.dispatch(
          registryFinalTokenApi.endpoints.getRegistryFinalTokenDetails.initiate(
            { project_id: id }
          )
        );

      const { data: documentIdDetails } = await store.dispatch(
        registryProjectDetailsApi.endpoints.getRegistryProjectDocumentIds.initiate(
          data?.data?.[0]?._id
        )
      );

      const docIds = [...new Set(documentIdDetails?.docids?.map(Number) || [])];
      const { data: documentListData } = await store.dispatch(
        registryDocumentApi.endpoints.getRegistryDocumentDetails.initiate({
          docids: JSON.stringify(docIds),
        })
      );

      // Filter out duplicate documents based on "doctype", "filename", and "hash"
      const uniqueDocumentList = documentListData?.reduce(
        (acc: DocumentData[], current: DocumentData) => {
          const isDuplicate = acc.some(
            (doc) =>
              doc.doctype === current.doctype &&
              doc.filename === current.filename &&
              doc.hash === current.hash
          );
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        },
        []
      );

      // Update state with filtered list
      setDocumentList(uniqueDocumentList);
      setYieldDataList(yieldData as YieldData[]);
      setCertificateDetails(finalTokenData);

      if (!isMethodDetailsLoading) {
        setMethodData(methodDetails?.data);
      }
    } catch {
    } finally {
      setIsSpinning(false);
    }
  }

  async function getOrgIdsByAssigneeType(
    record: ProjectDetailsResponse | undefined,
    assigneeType: string
  ): Promise<JSX.Element> {
    const filteredAssignments = record?.data?.[0]?.assignments?.filter(
      (assignment) => assignment?.assignee_type === assigneeType
    );
    const orgID = filteredAssignments?.map(
      (assignment) => assignment?.organization
    );
    return <RegistryDisplayOrganizationName orgid={orgID} place="table" />;
  }

  const [verifier, setVerifier] = useState<JSX.Element | null>(null);
  const [validator, setValidator] = useState<JSX.Element | null>(null);
  const [sponsor, setSponsor] = useState<JSX.Element | null>(null);

  useEffect(() => {
    console.log("isLoading....", isLoading);
    if (!isLoading) {
      fetchData();
      getOrgIdsByAssigneeType(data, "Verifier").then((result) =>
        setVerifier(result)
      );
      getOrgIdsByAssigneeType(data, "Validator").then((result) =>
        setValidator(result)
      );
      getOrgIdsByAssigneeType(data, "Project Sponsor").then((result) =>
        setSponsor(result)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]);

  const onDownloadDocument = (record: DocumentData): void => {
    axios
      .get("/rs/registry/registrydocument/" + record.ID, {
        responseType: "blob",
      })
      .then((blob) => {
        console.log("blob", blob);
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", record.filename);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode?.removeChild(link);
      });
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value == null || value === 0) return "-";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const YieldColumns: ColumnsType<YieldData> = [
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      sorter: (a, b) => a.period.localeCompare(b.period),
      defaultSortOrder: "ascend",
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },

    {
      title: "Estimated Yield (tCo2e)",
      dataIndex: "co2_estimated",
      key: "co2_estimated",
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: (
        <span>
          Issued Credits (tCo2e)
          <Popover content="Amount of issued credits for this project per GHG yield period, expressed in total amount of tCO2e.">
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Popover>
        </span>
      ),
      dataIndex: "co2_realized",
      key: "co2_realized",
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: "Issued Credit IDs",
      dataIndex: "yield_id",
      key: "credit_ids",
      width: 210,
      render: (text: YieldIdItem[], record: YieldData) => {
        // Process yield_id array
        const yieldIds =
          record.yield_id
            ?.filter((item) => item.token_id && item.co2_realized) // Filter valid items
            .map((item) => `NFRY-${item.token_id}`) || [];

        // Display logic
        if (yieldIds.length === 0) {
          return (
            <Typography.Text style={{ fontSize: 16 }}>
              -
            </Typography.Text>
          );
        } else {
          return (
            <Popover
              content={
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {record.yield_id
                    ?.filter((item) => item.token_id && item.co2_realized)
                    .map((item, index) => {
                      const { token_id, co2_realized } = item;
                      const idText = `NFRY-${token_id} containing ${co2_realized} credits within the range (NFRY-${token_id}:1 - NFRY-${token_id}:${co2_realized})`;
                      return (
                        <Typography.Text
                          key={index}
                          style={{
                            display: "block",
                            fontSize: 14,
                          }}
                        >
                          • {idText}
                        </Typography.Text>
                      );
                    })}
                </div>
              }
              title="All Credit IDs"
              trigger="click"
            >
              <Typography.Text
                style={{
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {yieldIds.slice(0, 2).join(", ")}{" "}
                {yieldIds.length > 0 && (
                  <span
                    style={{
                      fontSize: 12,
                      color: "#08979C",
                    }}
                  >
                    … [Show More]
                  </span>
                )}
              </Typography.Text>
            </Popover>
          );
        }
      },
    },
    {
      title: "Available Pre-Credits",
      dataIndex: "pre_credits",
      key: "pre_credits",
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: "Available Credits",
      dataIndex: "credits",
      key: "credits",
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: "Storage Duration (Years)",
      dataIndex: "storage_duration",
      key: "storage_duration",
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
  ];

  const certificateRetirementColumns: ColumnsType<CertificateDetail> = [
    {
      title: "Retirement Date",
      dataIndex: "updated",
      key: "updated",
      sorter: (a, b) => {
        const dateA = a.updated?.$date || a.created?.$date || "";
        const dateB = b.updated?.$date || b.created?.$date || "";
        return dateA.localeCompare(dateB);
      },
      width: 200,
      defaultSortOrder: "descend",
      render: (updated: DateObject, record: CertificateDetail) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            <span>
              {updated
                ? moment.utc(updated["$date"]).local().format("DD MMM, YYYY")
                : record?.created
                ? moment
                    .utc(record?.created["$date"])
                    .local()
                    .format("DD MMM, YYYY")
                : undefined}
            </span>
          </Typography.Text>
        );
      },
    },
    {
      title: (
        <span>
          Retired Credit IDs
          <Popover
            content={
              <>
                Dockal Credits are transferred and claimed in credit bundles
                (Dockal Tradable Credit Bundles).
                <br />
                Each credit bundle has a specific ID.
                <br />A beneficiary has the option to claim a full bundle or a
                part of it.
              </>
            }
          >
            <span className="info-icon" style={{ marginLeft: 5 }}>
              <InfoCircleOutlined />
            </span>
          </Popover>
        </span>
      ),
      dataIndex: "finaltoken_id",
      key: "finaltoken_id",
      width: 250,
      render: (text: string, record: CertificateDetail) => {
        const tokenCount = record.no_of_token || 0;

        if (!text || tokenCount === 0) {
          return (
            <Typography.Text style={{ fontSize: 16 }}>
              -
            </Typography.Text>
          );
        }

        const fullText = `PTCB-${text} containing ${tokenCount} credits within the range (PTCB-${text}:1 - PTCB-${text}:${tokenCount})`;

        return (
          <Popover
            content={
              <Typography.Text
                style={{
                  fontSize: 14,
                  whiteSpace: "nowrap",
                }}
              >
                {fullText}
              </Typography.Text>
            }
            title="Retired Credit Details"
            trigger="click"
          >
            <Typography.Text
              style={{
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              PTCB-{text}{" "}
              <span
                style={{
                  fontSize: 12,
                  color: "#08979C",
                }}
              >
                … [Show More]
              </span>
            </Typography.Text>
          </Popover>
        );
      },
    },
    {
      title: "Retired Credits",
      dataIndex: "no_of_token",
      key: "no_of_token",
      width: 200,
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Beneficiary",
      dataIndex: "beneficiary_name",
      key: "beneficiary_name",
      width: 250,
      render: (record: string) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              style={{ backgroundColor: "#87d068", marginRight: "8px" }}
              icon={<ShopOutlined />}
            />
            {record}
          </div>
        );
      },
    },
    {
      title: "Retirement Purpose",
      dataIndex: "certificate_retirement_purpose",
      key: "certificate_retirement_purpose",
      width: 300,
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Period",
      dataIndex: "yield_period",
      width: 200,
      key: "certificate_yield_periods",
      render: (record: string[]) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record && record.length > 0
              ? Array.from(new Set(record)).join(", ")
              : ""}
          </Typography.Text>
        );
      },
    },
  ];

  const certificateCancelledColumns: ColumnsType<CertificateDetail> = [
    {
      title: "Cancelled Date",
      dataIndex: "updated",
      key: "updated",
      sorter: (a, b) => {
        const dateA = a.updated?.$date || a.created?.$date || "";
        const dateB = b.updated?.$date || b.created?.$date || "";
        return dateA.localeCompare(dateB);
      },
      width: 200,
      defaultSortOrder: "descend",
      render: (updated: DateObject, record: CertificateDetail) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            <span>
              {updated
                ? moment.utc(updated["$date"]).local().format("DD MMM, YYYY")
                : record?.created
                ? moment
                    .utc(record?.created["$date"])
                    .local()
                    .format("DD MMM, YYYY")
                : undefined}
            </span>
          </Typography.Text>
        );
      },
    },
    {
      title: <span>Cancelled Credit IDs</span>,
      dataIndex: "finaltoken_id",
      key: "finaltoken_id",
      width: 250,
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            PTCB - {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Cancelled Credits",
      dataIndex: "no_of_token",
      key: "no_of_token",
      width: 200,
      render: (record: number) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },

    {
      title: "Cancellation Reason",
      dataIndex: "certificate_retirement_purpose",
      key: "certificate_retirement_purpose",
      width: 300,
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Period",
      dataIndex: "yield_period",
      width: 200,
      key: "certificate_yield_periods",
      render: (record: string[]) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record && record.length > 0
              ? Array.from(new Set(record)).join(", ")
              : ""}
          </Typography.Text>
        );
      },
    },
  ];

  const documentColumns: ColumnsType<DocumentData> = [
    {
      title: "Document Type",
      dataIndex: "doctype",
      key: "doctype",
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Document Name",
      dataIndex: "filename",
      key: "filename",
      render: (record: string) => {
        return (
          <Typography.Text style={{ fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      key: "actions",
      title: "Download",
      render: (record: DocumentData) => {
        return (
          <>
            <Row gutter={[10, 10]}>
              <Col>
                <DownloadOutlined
                  onClick={() => {
                    onDownloadDocument(record);
                  }}
                />
              </Col>
            </Row>
          </>
        );
      },
    },
  ];

  return (
    <div>
      {!isLoading && (
        <Spin
          size="large"
          spinning={isSpinning}
          indicator={
            <LoadingOutlined style={{ fontSize: 24, color: "#08979C" }} spin />
          }
        >
          <div style={{ minHeight: "80vh" }}>
            {/* Header Section */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 24,
              }}
            >
              <div>
                <Typography.Title level={2} style={{ margin: 0 }}>
                  {data?.data?.[0]?.name}
                </Typography.Title>
                <Typography.Paragraph
                  style={{ fontSize: 14, marginTop: 8, marginBottom: 0, color: 'rgba(0, 0, 0, 0.45)' }}
                >
                  {data?.data?.[0]?.ghg_project_summary}
                </Typography.Paragraph>
              </div>
              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  onClick={handleGoBack}
                  style={{ backgroundColor: "#08979C", color: "white" }}
                >
                  Back
                </Button>
              </div>
            </div>
            {/* Project Information Card */}
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 8,
                background: "#ffffff",
                borderLeft: "4px solid #08979C",
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0',
              }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                Project Information
              </Typography.Title>
              <Row gutter={[24, 20]}>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Project ID
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {id}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Project Status
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {data?.data?.[0]?.status}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Project Developer
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    <RegistryDisplayOrganizationName
                      orgid={data?.data?.[0]?.organization}
                      place="details"
                    />
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Standards Used
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {data?.data?.[0]?.standards_used || "Not specified"}
                  </Typography.Text>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Methodology
                    </Typography.Text>
                  </div>
                  {methodData?.length ? (
                    methodData.length === 1 ? (
                      <Typography.Text strong style={{ fontSize: 14 }}>
                        {methodData[0].name}
                      </Typography.Text>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {methodData.map((item, index) => (
                          <li key={index}>
                            <Typography.Text strong style={{ fontSize: 14 }}>
                              {item.name}
                            </Typography.Text>
                          </li>
                        ))}
                      </ul>
                    )
                  ) : (
                    <Typography.Text strong style={{ fontSize: 14 }}>
                      Not specified
                    </Typography.Text>
                  )}
                </Col>
              </Row>
            </Card>

            {/* Assignment & Timeline Card */}
            <Card
              style={{
                marginBottom: 16,
                borderRadius: 8,
                background: "#ffffff",
                borderLeft: "4px solid #08979C",
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0',
              }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Typography.Title level={5} style={{ marginBottom: 12 }}>
                Assignment & Timeline
              </Typography.Title>
              <Row gutter={[24, 20]}>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Project Sponsor
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {sponsor || "-"}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Validator
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {validator || "-"}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Verifier
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {verifier || "-"}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Crediting Period
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {(() => {
                      const value = data?.data?.[0]?.crediting_period;
                      if (!value || !Array.isArray(value) || value.length < 2) return "Not specified";
                      const formatDate = (dateStr: string) =>
                        moment(new Date(dateStr)).format("MMM D, YYYY");
                      return `${formatDate(value[0])} - ${formatDate(value[1])}`;
                    })()}
                  </Typography.Text>
                </Col>
                <Col span={6}>
                  <div style={{ marginBottom: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      Project Period
                    </Typography.Text>
                  </div>
                  <Typography.Text strong style={{ fontSize: 14 }}>
                    {(() => {
                      const value = data?.data?.[0]?.project_period;
                      if (!value || !Array.isArray(value) || value.length < 2) return "Not specified";
                      const formatDate = (dateStr: string) =>
                        moment(new Date(dateStr)).format("MMM D, YYYY");
                      return `${formatDate(value[0])} - ${formatDate(value[1])}`;
                    })()}
                  </Typography.Text>
                </Col>
              </Row>
            </Card>
            {/* GHG Yield Information */}
            <Typography.Title
              level={4}
              style={{
                marginTop: 32,
                marginBottom: 16,
              }}
            >
              GHG Yield Information, Issued and Available Credits
            </Typography.Title>
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
              {yieldDataList && yieldDataList.length > 0 ? (
                yieldDataList.map((record: YieldData, index: number) => {
                  const yieldIds = record.yield_id
                    ?.filter((item) => item.token_id && item.co2_realized)
                    .map((item) => `NFRY-${item.token_id}`) || [];

                  return (
                    <Card
                      key={index}
                      style={{
                        borderRadius: 8,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0',
                      }}
                      bodyStyle={{ padding: '20px 24px' }}
                    >
                      <Row gutter={16} align="middle" wrap={false}>
                        {/* Period */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Period
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.period}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Estimated Yield */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Estimated Yield (tCo2e)
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {formatNumber(record.co2_estimated)}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Issued Credits */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Issued Credits (tCo2e)
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {formatNumber(record.co2_realized)}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Issued Credit IDs */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Issued Credit IDs
                            </Typography.Text>
                            {yieldIds.length === 0 ? (
                              <Typography.Text strong style={{ fontSize: 13 }}>
                                -
                              </Typography.Text>
                            ) : (
                              <Popover
                                content={
                                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                                    {record.yield_id
                                      ?.filter((item) => item.token_id && item.co2_realized)
                                      .map((item, idx) => {
                                        const { token_id, co2_realized } = item;
                                        const idText = `NFRY-${token_id} containing ${co2_realized} credits within the range (NFRY-${token_id}:1 - NFRY-${token_id}:${co2_realized})`;
                                        return (
                                          <Typography.Text
                                            key={idx}
                                            style={{
                                              display: "block",
                                              fontSize: 14,
                                            }}
                                          >
                                            • {idText}
                                          </Typography.Text>
                                        );
                                      })}
                                  </div>
                                }
                                title="All Credit IDs"
                                trigger="click"
                              >
                                <Typography.Text
                                  strong
                                  style={{
                                    fontSize: 13,
                                    cursor: "pointer",
                                  }}
                                >
                                  {yieldIds.slice(0, 2).join(", ")}{" "}
                                  {yieldIds.length > 2 && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "#08979C",
                                      }}
                                    >
                                      … [+{yieldIds.length - 2} more]
                                    </span>
                                  )}
                                </Typography.Text>
                              </Popover>
                            )}
                          </div>
                        </Col>

                        {/* Available Pre-Credits */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Available Pre-Credits
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {formatNumber(record.pre_credits)}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Available Credits */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Available Credits
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {formatNumber(record.credits)}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Storage Duration */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Storage Duration (Years)
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.storage_duration}
                            </Typography.Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  );
                })
              ) : (
                <Card style={{ borderRadius: 8 }}>
                  <Empty description="No yield data available" />
                </Card>
              )}
            </Space>

            {/* Retirement Details */}
            <Typography.Title
              level={4}
              style={{ marginTop: 32, marginBottom: 8 }}
            >
              Retirement Details
            </Typography.Title>
            <Typography.Paragraph
              style={{ fontSize: 14, marginBottom: 16, color: 'rgba(0, 0, 0, 0.45)' }}
            >
              Dockal Credits are retired when a retiring entity or a
              beneficiary makes a claim. Here you can view the Dockal Credits
              that have been retired for this project.
            </Typography.Paragraph>
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
              {certificateDetails?.data && certificateDetails.data.length > 0 ? (
                certificateDetails.data.map((record: CertificateDetail, index: number) => {
                  const updated = record.updated || record.created;
                  const tokenCount = record.no_of_token || 0;
                  const fullText = record.finaltoken_id
                    ? `PTCB-${record.finaltoken_id} containing ${tokenCount} credits within the range (PTCB-${record.finaltoken_id}:1 - PTCB-${record.finaltoken_id}:${tokenCount})`
                    : "-";

                  return (
                    <Card
                      key={index}
                      style={{
                        borderRadius: 8,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0',
                      }}
                      bodyStyle={{ padding: '20px 24px' }}
                    >
                      <Row gutter={16} align="middle" wrap={false}>
                        {/* Retirement Date */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Retirement Date
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {updated
                                ? moment.utc(updated["$date"]).local().format("DD MMM, YYYY")
                                : "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Retired Credit IDs */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Retired Credit IDs
                            </Typography.Text>
                            {!record.finaltoken_id || tokenCount === 0 ? (
                              <Typography.Text strong style={{ fontSize: 13 }}>
                                -
                              </Typography.Text>
                            ) : (
                              <Popover
                                content={
                                  <Typography.Text style={{ fontSize: 14, whiteSpace: "nowrap" }}>
                                    {fullText}
                                  </Typography.Text>
                                }
                                title="Retired Credit Details"
                                trigger="click"
                              >
                                <Typography.Text
                                  strong
                                  style={{
                                    fontSize: 13,
                                    cursor: "pointer",
                                  }}
                                >
                                  PTCB-{record.finaltoken_id}{" "}
                                  <span style={{ fontSize: 11, color: "#08979C" }}>
                                    … [Show More]
                                  </span>
                                </Typography.Text>
                              </Popover>
                            )}
                          </div>
                        </Col>

                        {/* Retired Credits */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Retired Credits
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.no_of_token || "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Beneficiary */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Beneficiary
                            </Typography.Text>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <Avatar
                                style={{ backgroundColor: "#87d068", marginRight: "8px" }}
                                icon={<ShopOutlined />}
                                size="small"
                              />
                              <Typography.Text strong style={{ fontSize: 13 }}>
                                {record.beneficiary_name || "-"}
                              </Typography.Text>
                            </div>
                          </div>
                        </Col>

                        {/* Retirement Purpose */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Retirement Purpose
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.certificate_retirement_purpose || "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Period */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Period
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.yield_period && record.yield_period.length > 0
                                ? Array.from(new Set(record.yield_period)).join(", ")
                                : "-"}
                            </Typography.Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  );
                })
              ) : (
                <Card style={{ borderRadius: 8 }}>
                  <Empty description="No retirement data available" />
                </Card>
              )}
            </Space>

            {/* Cancellation Details */}
            <Typography.Title
              level={4}
              style={{ marginTop: 32, marginBottom: 16 }}
            >
              Cancellation Details
            </Typography.Title>
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
              {false ? (
                [].map((record: CertificateDetail, index: number) => {
                  const updated = record.updated || record.created;

                  return (
                    <Card
                      key={index}
                      style={{
                        borderRadius: 8,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0',
                      }}
                      bodyStyle={{ padding: '20px 24px' }}
                    >
                      <Row gutter={16} align="middle" wrap={false}>
                        {/* Cancelled Date */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Cancelled Date
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {updated
                                ? moment.utc(updated["$date"]).local().format("DD MMM, YYYY")
                                : "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Cancelled Credit IDs */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Cancelled Credit IDs
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.finaltoken_id ? `PTCB - ${record.finaltoken_id}` : "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Cancelled Credits */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Cancelled Credits
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.no_of_token || "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Cancellation Reason */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Cancellation Reason
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.certificate_retirement_purpose || "-"}
                            </Typography.Text>
                          </div>
                        </Col>

                        {/* Period */}
                        <Col flex="1">
                          <div>
                            <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                              Period
                            </Typography.Text>
                            <Typography.Text strong style={{ fontSize: 13 }}>
                              {record.yield_period && record.yield_period.length > 0
                                ? Array.from(new Set(record.yield_period)).join(", ")
                                : "-"}
                            </Typography.Text>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  );
                })
              ) : (
                <Card style={{ borderRadius: 8 }}>
                  <Empty description="No cancellation data available" />
                </Card>
              )}
            </Space>

            {/* Project Documents */}
            <Typography.Title
              level={4}
              style={{ marginTop: 32, marginBottom: 16 }}
            >
              Project Documents
            </Typography.Title>
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 24 }}>
              {documentList && documentList.length > 0 ? (
                documentList.map((record: DocumentData, index: number) => (
                  <Card
                    key={index}
                    style={{
                      borderRadius: 8,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                      border: '1px solid #f0f0f0',
                    }}
                    bodyStyle={{ padding: '20px 24px' }}
                  >
                    <Row gutter={16} align="middle" wrap={false}>
                      {/* Document Type */}
                      <Col flex="1">
                        <div>
                          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                            Document Type
                          </Typography.Text>
                          <Typography.Text strong style={{ fontSize: 13 }}>
                            {record.doctype || "-"}
                          </Typography.Text>
                        </div>
                      </Col>

                      {/* Document Name */}
                      <Col flex="2">
                        <div>
                          <Typography.Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>
                            Document Name
                          </Typography.Text>
                          <Typography.Text strong style={{ fontSize: 13 }}>
                            {record.filename || "-"}
                          </Typography.Text>
                        </div>
                      </Col>

                      {/* Download Action */}
                      <Col flex="0 0 auto">
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          onClick={() => onDownloadDocument(record)}
                          style={{
                            backgroundColor: "#08979C",
                            borderColor: "#08979C",
                          }}
                        >
                          Download
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))
              ) : (
                <Card style={{ borderRadius: 8 }}>
                  <Empty description="No documents available" />
                </Card>
              )}
            </Space>
          </div>
        </Spin>
      )}
    </div>
  );
};

export default RegistryProjectDetailsPageView;
